/*
  API-Funktionen für den neuen Solver (SOLVER_REWRITE.md, M7).

  Dünne Aufrufe auf /api/shiftplan/solver/runs — gerechnet wird im Worker,
  hier werden Läufe nur eingestellt, beobachtet und übernommen/verworfen.

  Die Vorschau kommt als Scheduler-artige Nutzlast (gleiche Formen wie der
  /shiftplan-Bootstrap) und wird hier mit denselben Mappern in die
  Plan-Domänenformen übersetzt — die Vorschau-Grafik ist dadurch 1:1 das
  Raster von /shiftplan. Strafpunkte/Diagnose liefert das Backend nur für
  Super_Admin (Grill-Entscheide 2026-08-06).
*/

import httpClient from "./httpClient";
import {
  mapAbsenceOptions,
  mapDemandPayload,
  mapDepartmentOptions,
  mapPublicHolidays,
  mapShiftOptions,
  mapUserHolidays,
  normalizeSchedulerEvent,
  parseSchedulerUserRow,
} from "./mappers";
import { normalizeShiftDisplayMode } from "../domain/model/shiftDisplay";

export const SOLVER_OBJECTIVE_SCALE = 60; // 60 interne Einheiten = 1 Katalog-Punkt (AE-024)

function mapConstraintCost(raw) {
  return {
    nbNumber: raw?.nb_number,
    costTotal: Number(raw?.cost_total || 0),
    violationCount: Number(raw?.violation_count || 0),
    costPoints: Math.round(Number(raw?.cost_total || 0) / SOLVER_OBJECTIVE_SCALE),
  };
}

function mapProgress(raw) {
  if (!raw) return null;
  const elapsed = Number(raw.elapsed_seconds);
  const limit = Number(raw.time_limit_seconds);
  return {
    elapsedSeconds: Number.isFinite(elapsed) ? elapsed : null,
    timeLimitSeconds: Number.isFinite(limit) && limit > 0 ? limit : null,
  };
}

export function mapSolverRun(raw) {
  if (!raw) return null;
  return {
    id: raw.id,
    status: raw.status,
    startDate: raw.start_date,
    endDate: raw.end_date,
    existingMode: raw.existing_mode,
    locationIds: raw.location_ids || [],
    locationNames: raw.location_names || [],
    excludedUserIds: raw.excluded_user_ids || [],
    excludedDepartmentIds: raw.excluded_department_ids || [],
    excludedShiftIds: raw.excluded_shift_ids || [],
    createdAt: raw.created_at,
    createdByName: raw.created_by_name || "",
    transferredByName: raw.transferred_by_name || "",
    errorMessage: raw.error_message,
    failureHints: raw.failure_hints || [],
    timeLimitSeconds: raw.time_limit_seconds,
    startedAt: raw.started_at,
    finishedAt: raw.finished_at,
    wallTimeSeconds: raw.wall_time_seconds,
    cancelRequested: Boolean(raw.cancel_requested),
    transferredAt: raw.transferred_at,
    progress: mapProgress(raw.progress),
    // Ab hier Super_Admin-only — für alle anderen liefert das Backend nichts.
    solverStatus: raw.solver_status ?? null,
    objectiveValue: raw.objective_value ?? null,
    objectivePoints:
      raw.objective_value == null ? null : Math.round(raw.objective_value / SOLVER_OBJECTIVE_SCALE),
    constraintCosts: (raw.constraint_costs || []).map(mapConstraintCost),
    diagnostics: raw.diagnostics || null, // { live, elapsed_seconds, stats, log_tail, incumbents }
  };
}

function mapLocationOption(raw) {
  return { id: Number(raw?.id), name: String(raw?.name || "") };
}

/*
  Vorschau-Nutzlast → Plan-Domänenformen. Entspricht mapSchedulerPayload,
  nur ohne Editor-Anteile; `solverSource` ("solver"/"fixed") steuert das
  Abblassen des fixierten Bestands im Raster.
*/
export function mapSolverPreview(raw) {
  if (!raw) return null;
  const colorMap = raw.color_map || {};
  const employees = (Array.isArray(raw.user_list) ? raw.user_list : [])
    .map(parseSchedulerUserRow)
    .filter((employee) => employee.id && !employee.isPlaceholder);
  const employeesById = employees.reduce((accumulator, employee) => {
    accumulator[employee.id] = employee;
    return accumulator;
  }, {});
  const shiftOptionsById = mapShiftOptions(raw.shift_list);

  const entries = (Array.isArray(raw.events) ? raw.events : [])
    .map((event) => {
      const entry = normalizeSchedulerEvent(event, employeesById, shiftOptionsById, colorMap);
      if (!entry) return null;
      return {
        ...entry,
        solverSource: event?.solver_source || null,
        // Rein visuell: die Vorschau zeigt keine „nicht freigegeben"-Icons
        // (Wunsch 2026-08-06) — übertragen wird trotzdem als Entwurf.
        releaseStatus: "released",
        isDraft: false,
      };
    })
    .filter(Boolean);

  return {
    startDate: raw.start_date,
    endDate: raw.end_date,
    employees,
    entries,
    absenceOptions: mapAbsenceOptions(raw.absence_list),
    departments: mapDepartmentOptions(raw.department_list),
    userHolidays: mapUserHolidays(raw.user_holiday_list),
    publicHolidays: mapPublicHolidays(raw.public_holidays),
    demandEntries: mapDemandPayload(raw),
    workingDays: Number(raw.working_days || 5),
    shiftDisplayMode: normalizeShiftDisplayMode(raw.shift_display_mode),
  };
}

export async function createSolverRun({
  startDate,
  endDate,
  existingMode,
  locationIds,
  excludedUserIds,
  excludedDepartmentIds,
  excludedShiftIds,
  timeLimitSeconds,
}) {
  const response = await httpClient.post("/api/shiftplan/solver/runs", {
    start_date: startDate,
    end_date: endDate,
    existing_mode: existingMode,
    location_ids: (locationIds || []).map((id) => Number(id)),
    excluded_user_ids: (excludedUserIds || []).map((id) => Number(id)),
    excluded_department_ids: (excludedDepartmentIds || []).map((id) => Number(id)),
    excluded_shift_ids: (excludedShiftIds || []).map((id) => Number(id)),
    time_limit_seconds: timeLimitSeconds || null,
  });
  return mapSolverRun(response.data);
}

/*
  MA/Abteilungen/Schichten je sichtbarem Standort — Datenbasis der
  Ausschluss-Picker. Ein MA mit mehreren Standorten erscheint in jeder Gruppe.
*/
export async function getSolverDirectory() {
  const response = await httpClient.get("/api/shiftplan/solver/directory");
  return (response.data?.locations || []).map((location) => ({
    id: Number(location?.id),
    name: String(location?.name || ""),
    users: (location?.users || []).map((row) => ({
      id: Number(row.id),
      name: String(row.name || ""),
      // Einstellung "Manuelle Planung" in /team: diese Person plant der Solver
      // grundsaetzlich nicht (solver/io/loader.py).
      manualPlanning: Boolean(row.manual_planning),
    })),
    departments: (location?.departments || []).map((row) => ({ id: Number(row.id), name: String(row.name || "") })),
    shifts: (location?.shifts || []).map((row) => ({
      id: Number(row.id),
      name: String(row.name || ""),
      timeLabel: String(row.time_label || ""),
    })),
  }));
}

export async function listSolverRuns() {
  const response = await httpClient.get("/api/shiftplan/solver/runs");
  return {
    runs: (response.data?.runs || []).map(mapSolverRun),
    availableLocations: (response.data?.available_locations || []).map(mapLocationOption),
    currentLocationId: response.data?.current_location_id ?? null,
    // true/false vom Backend (nur Super_Admin), sonst null = keine Aussage.
    workerAlive: typeof response.data?.worker_alive === "boolean" ? response.data.worker_alive : null,
  };
}

export async function getSolverRun(runId) {
  const response = await httpClient.get(`/api/shiftplan/solver/runs/${runId}`);
  return mapSolverRun(response.data);
}

export async function getSolverRunPreview(runId) {
  const response = await httpClient.get(`/api/shiftplan/solver/runs/${runId}/preview`);
  return {
    run: mapSolverRun(response.data?.run),
    preview: mapSolverPreview(response.data?.preview),
  };
}

export async function transferSolverRun(runId) {
  const response = await httpClient.post(`/api/shiftplan/solver/runs/${runId}/transfer`);
  return {
    transferred: Number(response.data?.transferred || 0),
    conflicts: response.data?.conflicts || [],
  };
}

export async function discardSolverRun(runId) {
  const response = await httpClient.post(`/api/shiftplan/solver/runs/${runId}/discard`);
  return mapSolverRun(response.data);
}

export async function cancelSolverRun(runId) {
  const response = await httpClient.post(`/api/shiftplan/solver/runs/${runId}/cancel`);
  return mapSolverRun(response.data);
}

/* Entfernt den Lauf samt Zwischenergebnis. Bereits übernommene Einträge im
   Schichtplan bleiben davon unberührt. */
export async function deleteSolverRun(runId) {
  const response = await httpClient.delete(`/api/shiftplan/solver/runs/${runId}`);
  return Number(response.data?.deleted || runId);
}

/*
  Kennzahlen VOR dem Start ("Umfang dieses Laufs", Redesign 2026-08-26) --
  reine Zaehlung aus dem bereits gepflegten Bedarf/Zeitplan, keine Deckungs-
  oder Machbarkeits-Vorhersage (Grill-Entscheid: Rohzahlen sagen nichts ueber
  echte CP-SAT-Machbarkeit aus).
*/
export async function getSolverScopePreview({
  startDate,
  endDate,
  locationIds,
  existingMode,
  excludedDepartmentIds,
  excludedShiftIds,
}) {
  const params = new URLSearchParams();
  params.set("start_date", startDate);
  params.set("end_date", endDate);
  if ((locationIds || []).length) params.set("location_ids", locationIds.join(","));
  if (existingMode) params.set("existing_mode", existingMode);
  if ((excludedDepartmentIds || []).length) params.set("excluded_department_ids", excludedDepartmentIds.join(","));
  if ((excludedShiftIds || []).length) params.set("excluded_shift_ids", excludedShiftIds.join(","));

  const response = await httpClient.get(`/api/shiftplan/solver/scope-preview?${params.toString()}`);
  const data = response.data || {};
  return {
    days: Number(data.days || 0),
    shiftSlots: Number(data.shift_slots || 0),
    departmentCount: Number(data.department_count || 0),
    absenceCount: Number(data.absence_count || 0),
    holidayCount: Number(data.holiday_count || 0),
    unavailableCount: Number(data.unavailable_count || 0),
    keptExisting: Number(data.kept_existing || 0),
    toPlan: Number(data.to_plan || 0),
  };
}

/*
  Zusammenarbeits-Regeln ("Regeln & Ausschluesse") -- eine Zeile pro Paar,
  Verwaltung bleibt zusaetzlich ueber die Profilseite jedes Mitarbeitenden
  moeglich (client/src/scenes/team/edit_user.jsx); dies ist eine aggregierte
  Ansicht ueber dieselben Daten.
*/
function mapPairRule(raw) {
  return {
    id: String(raw?.id || ""),
    userId: Number(raw?.user_id),
    userName: String(raw?.user_name || ""),
    employeeId: Number(raw?.employee_id),
    employeeName: String(raw?.employee_name || ""),
    preferenceType: raw?.preference_type === "avoidable" ? "avoidable" : "preferred",
  };
}

export async function listPairRules() {
  const response = await httpClient.get("/api/shiftplan/solver/pair-rules");
  return (response.data?.pairs || []).map(mapPairRule);
}

export async function createPairRule({ userId, employeeId, preferenceType }) {
  const response = await httpClient.post("/api/shiftplan/solver/pair-rules", {
    user_id: Number(userId),
    employee_id: Number(employeeId),
    preference_type: preferenceType,
  });
  return mapPairRule(response.data);
}

export async function deletePairRule(pairId) {
  await httpClient.delete(`/api/shiftplan/solver/pair-rules/${encodeURIComponent(pairId)}`);
  return pairId;
}
